/**
MIT License

Copyright (c) 2025 Alex Lowe

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
 */

// salary-calculator-tool

import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import Wizard from './features/wizard/Wizard';
import { ConfigProvider, Layout, Menu, Typography, Alert, Checkbox, Grid, Collapse, theme as antdTheme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
// antd swap out here
import { ChakraProvider } from '@chakra-ui/react';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { calculateSalaryDetails } from './salaryCalculations'; //calculateDebugOutput
import CalculatorForm from './components/CalculatorForm';
import ResultsTable from './components/ResultsTable';
import CalculatorTable from './components/CalculatorTable';
import CalculatorChart from './components/CalculatorChart';
import Methodology from './Methodology';
import CoffeeWidget from './components/CoffeeWidget';
import 'react-resizable/css/styles.css';
import TaxYearSelector from './components/TaxYearSelector';
import RsuVestInputs from './components/RsuVestInputs';
import { TAX_YEARS } from './constants/taxConstants';
import { calculateProgressivePAYE } from './utils/payeCalculations';
import StudentLoanInputs from './components/StudentLoanInputs';
import TargetNetSalaryCalculator from './components/TargetNetSalaryCalculator';
import StickyCalculatorInputs from './components/StickyCalculatorInputs';
const AssistantLauncherLazy = lazy(() => import('./assistant/AssistantLauncher'));

// Lazy-load FeedbackOffers (Tally + Stripe only on that route)
const FeedbackOffers = React.lazy(() => import('./FeedbackOffers'));

const { Header, Content, Footer } = Layout;
const { Title } = Typography;
const { Panel } = Collapse;
const { useBreakpoint } = Grid;
const { Text } = Typography;

const customTheme = {
  token: {
    colorPrimary: '#1976d2',
    colorSecondary: '#dc004e',
    colorError: '#f44336',
    colorWarning: '#ff9800',
    colorInfo: '#2196f3',
    colorSuccess: '#4caf50',
    colorBgBase: '#f5f5f5',
    colorTextBase: '#000000',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    borderRadius: 8,
  },
};

// const theme = extendTheme({
//   colors: {
//     primary: {
//       50: '#e3f2fd',
//       100: '#bbdefb',
//       500: '#1976d2',
//       600: '#1565c0',
//       700: '#0d47a1',
//     },
//     success: { 500: '#34c759' },
//     warning: { 500: '#ff9500' },
//     error: { 500: '#ff3b30' },
//   },
//   fonts: {
//     body: 'Inter, system-ui, sans-serif',
//     heading: 'Inter, system-ui, sans-serif',
//   },
//   components: {
//     Button: {
//       baseStyle: {
//         fontWeight: 'medium',
//         borderRadius: 'md',
//       },
//     },
//     Card: {
//       baseStyle: {
//         container: {
//           borderRadius: 'lg',
//           boxShadow: 'md',
//         },
//       },
//     },
//   },
// });

function App() {
  const screens = useBreakpoint();
  const [selectedTaxYear, setSelectedTaxYear] = useState("2024/2025");
  const { token } = antdTheme.useToken();

  const [inputs, setInputs] = useState({
    isTaxResidentInScotland: false,
    baseSalary: '',
    bonus: '',
    medicalBenefit: '',
    voldeductions: '',
    rsus: '',
    capitalGains: '',
    rsuemployerNICrate: 6.9,
    employerMaxPensionContributionPct: 5,
    rsuTaxRate: 50,
    rsuVestEvents: [],                 // New field for RSU vest events
    studentLoanPlans: [],
    isEmployerNicLiable: true,
    employeePensionContribution: 0,
    employeePensionContributionMode: 'percentage', // 'percentage' or 'amount'
    employeePensionContributionPeriod: 'annual', // 'annual' or 'monthly'
  });

  useEffect(() => {
    // Only auto-change if the user hasn't interacted with the checkbox yet
    if (!inputs.employerNicManuallyChanged) {
      const is2025OrLater = selectedTaxYear === "2025/2026" || parseInt(selectedTaxYear.split('/')[0]) >= 2025;
      setInputs(prevInputs => ({
        ...prevInputs,
        isEmployerNicLiable: !is2025OrLater // Default to false for 2025/26 and later
      }));
    }
  }, [selectedTaxYear]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Tax year changed to:', selectedTaxYear);
    }
  }, [selectedTaxYear]);

  // Removed light/dark mode toggle per request

  // Update the handleInputChange function to handle RSU vest events
  const handleInputChange = (name, value) => {
    setInputs(prevInputs => ({
      ...prevInputs,
      [name]: value
    }));
    
    // If RSU vest events change, also update the total RSU amount and calculate an average NIC rate
    if (name === 'rsuVestEvents') {
      if (value && value.length > 0) {
        // Calculate total RSU amount
        const totalRsuAmount = value.reduce((sum, vest) => sum + vest.amount, 0);
        
        // Calculate total capital gains
        const totalCapitalGains = value.reduce((sum, vest) => 
          sum + (vest.sold_immediately ? 0 : vest.capital_gains), 0);
        
        // Calculate weighted average employer NIC rate
        const totalNICAmount = value.reduce((sum, vest) => 
          sum + (vest.amount * (vest.employer_nic_rate || 6.9) / 100), 0);
        
        const avgNICRate = totalRsuAmount > 0 ? 
          (totalNICAmount / totalRsuAmount) * 100 : 6.9;
        
        // Apply the progressive PAYE calculation to vest events
        const processedVestEvents = calculateProgressivePAYE(
          value,
          {
            baseSalary: inputs.baseSalary,
            bonus: inputs.bonus,
            // Other income details as needed
          },
          inputs.isTaxResidentInScotland,
          selectedTaxYear
        );
            
        setInputs(prevInputs => ({
          ...prevInputs,
          rsus: totalRsuAmount,
          capitalGains: totalCapitalGains,
          rsuemployerNICrate: parseFloat(avgNICRate.toFixed(1)),
          rsuVestEvents: processedVestEvents // Use the processed events with enhanced PAYE data
        }));
      } else {
        // Reset to defaults if no vest events
        setInputs(prevInputs => ({
          ...prevInputs,
          rsus: '',
          capitalGains: '',
          rsuemployerNICrate: 6.9
        }));
      }
    }
  };

    // Handle the employer NIC liability change
  const handleEmployerNicLiableChange = (isLiable) => {
    setInputs(prevInputs => ({
      ...prevInputs,
      isEmployerNicLiable: isLiable,
      employerNicManuallyChanged: true // Flag to indicate user manually changed this
    }));
  };

  const handleTaxResidencyChange = (event) => {
    setInputs(prevInputs => ({
      ...prevInputs,
      isTaxResidentInScotland: event.target.checked
    }));
  };

  const handleReset = () => {
    const is2025OrLater = selectedTaxYear === "2025/2026" || parseInt(selectedTaxYear.split('/')[0]) >= 2025;

    setInputs({
      isTaxResidentInScotland: false,
      baseSalary: '',
      bonus: '',
      medicalBenefit: '',
      voldeductions: '',
      rsus: '',
      capitalGains: '',
      rsuemployerNICrate: 6.9,
      employerMaxPensionContributionPct: 5,
      rsuVestEvents: [],
      studentLoanPlans: [],
      isEmployerNicLiable: !is2025OrLater,
      employerNicManuallyChanged: false
    });
  };

  const results = useMemo(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Recalculating with tax year:', selectedTaxYear);
    }
    return calculateSalaryDetails({
      ...inputs,
      employeePensionContribution: inputs.employeePensionContribution || 0
    }, selectedTaxYear);
  }, [inputs, selectedTaxYear]);

  const pensionLimitWarning = useMemo(() => {
    if (results.length > 0) {
      const maxPensionIndex = results.findIndex(result => parseFloat(result.total_pension_amount) >= 60000);
      
      if (maxPensionIndex !== -1) {
        // Since we're now using 0.5% increments (index/2), we need to display the actual percentage
        const maxPensionPct = results[maxPensionIndex].employee_pension_contribution_pct;
        
        return (
          <>
            Warning: at the level of gross pay inputted you will reach the £60,000 UK max pension contribution limit with an employee pension contribution % that is greater than{' '}
            <Text strong>{maxPensionPct.toFixed(1)}%</Text>. If you plan to do so, please make sure you have leftover allowances from previous years.
          </>
        );
      }
    }
    return null;
  }, [results]);

  return (
    <ConfigProvider theme={customTheme}>
      <BrowserRouter>
        <Layout className="layout" style={{ background: token.colorBgBase }}>
          <Header className="site-header" style={{ height: screens.xs ? 48 : 52, lineHeight: screens.xs ? '48px' : '52px', padding: '0 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 14, letterSpacing: 0.2, minWidth: screens.xs ? 0 : 140 }}>
                {/* Brand placeholder - leave blank until ready */}
              </div>
              <div className="nav-scroll" style={{ marginLeft: 'auto', maxWidth: '100%' }}>
                <Menu
                  theme="dark"
                  mode="horizontal"
                  defaultSelectedKeys={['1']}
                  style={{ lineHeight: screens.xs ? '48px' : '52px', background: 'transparent', fontSize: screens.xs ? 13 : 14 }}
                  disabledOverflow
                >
                  <Menu.Item key="1">
                    <NavLink to="/wizard?step=1" style={{ color: '#fff' }}>
                      <span style={{ fontWeight: 600 }}>Calculator Wizard</span>
                    </NavLink>
                  </Menu.Item>
                  <Menu.Item key="2">
                    <NavLink to="/methodology" style={{ color: '#fff' }}>
                      <span style={{ fontWeight: 600 }}>Methodology</span>
                    </NavLink>
                  </Menu.Item>
                  <Menu.Item key="3">
                    <NavLink to="/feedback" style={{ color: '#fff' }}>
                      <span style={{ fontWeight: 600 }}>Feedback</span>
                    </NavLink>
                  </Menu.Item>
                </Menu>
              </div>
            </div>
          </Header>
          <Content style={{ padding: '0 50px', background: token.colorBgContainer }}>
            <div className="site-layout-content">
              <Routes>
                {/* Make Wizard the focal/default route */}
                <Route path="/" element={<Wizard />} />
                {/* Keep legacy calculator accessible (hidden from nav) */}
                <Route path="/legacy" element={
                  <>
                    <Title>UK Salary Calculator</Title>
                    <Collapse>
                      <Panel 
                        header={
                          <span>
                            <InfoCircleOutlined /> Important Information
                          </span>
                        } 
                        key="1"
                      >
                        <Collapse ghost>
                          <Panel header="About this calculator" key="1.1">
                            <Alert
                              description="This calculator takes components of your pay and calculates all of the different components of UK tax, personal allowances, national insurance, etc. as standard in most calculators. Going a step further, I wanted to visualise how a change in the % contribution from an employee salary sacrifice scheme can impact the different outputs. You can therefore identify with which Employee Pension Contribution % you can: 1) Pay the least tax, 2) Maximise your Net Salary, etc."
                              type="info"
                              showIcon
                            />
                          </Panel>
                          <Panel header="Disclaimer" key="1.2">
                            <Alert
                              description="The numbers displayed in this tool will not be 100% accurate as the actual numbers will differ depending on when you vest RSUs, when you started employment during a tax year, etc."
                              type="warning"
                              showIcon
                            />
                          </Panel>
                          <Panel header="Important notes and future improvements" key="1.3">
                            <Alert
                              description={
                                <ul>
                                  <li>If looking for the amounts reflective of your month to month paychecks, you should toggle to use the data/charts 'excl RSU'. The 'incl RSU' numbers should be used to calculate how much you should salary sacrifice into your pension as these will be reflective if your end of year P60.</li>
                                  <li>The UK government caps total pension contributions at £60,000 per year. On the Chart there is a red reference line that displays when/if you have hit that limit. All numbers after that will be constant as you won't be able to contribute more to your pension (unless you are filling up previous years allowances).</li>
                                  <li>Student Loan/Postgraduate Loan impacts - still to add.</li>
                                </ul>
                              }
                              type="info"
                              showIcon
                            />
                          </Panel>
                        </Collapse>
                      </Panel>
                    </Collapse>
                    <TaxYearSelector 
                      selectedTaxYear={selectedTaxYear}
                      onTaxYearChange={(year) => {
                        console.log('Tax year selection changed to:', year);
                        setSelectedTaxYear(year);
                      }}
                    />
                    <CalculatorForm 
                      inputs={inputs}
                      onInputChange={handleInputChange}
                      onReset={handleReset}
                    />
                    <Checkbox
                      checked={inputs.isTaxResidentInScotland}
                      onChange={handleTaxResidencyChange}
                      style={{ marginBottom: 20 }}
                    >
                      Tax Resident in Scotland
                    </Checkbox>
                    <StudentLoanInputs 
                      selectedPlans={inputs.studentLoanPlans}
                      onChange={(plans) => handleInputChange('studentLoanPlans', plans)}
                      selectedTaxYear={selectedTaxYear}
                    />
                    <Title level={4}>Restricted Stock Units (RSUs)</Title>
                    <RsuVestInputs 
                      vestEvents={inputs.rsuVestEvents}
                      onChange={(events) => handleInputChange('rsuVestEvents', events)}
                      taxYear={selectedTaxYear}  // Pass the selected tax year
                      isTaxResidentInScotland={inputs.isTaxResidentInScotland} // Pass the Scottish tax residency status
                      isEmployerNicLiable={inputs.isEmployerNicLiable}
                      onEmployerNicLiableChange={handleEmployerNicLiableChange}  
                    />
                    {pensionLimitWarning && (
                      <Alert
                        message="Pension Contribution Limit Warning"
                        description={pensionLimitWarning}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 20 }}
                      />
                    )}
                    {results.length > 0 && (
                      <Alert
                        message={`Calculations based on ${selectedTaxYear} tax year rates`}
                        type="info"
                        showIcon
                        style={{ marginBottom: 20 }}
                      />
                    )}
                    {results.length > 0 && (
                      <>
                      <ResultsTable 
                        inputValues={inputs}
                        selectedTaxYear={selectedTaxYear}
                        rsuTaxRate={inputs.rsuTaxRate}
                      />
                      <TargetNetSalaryCalculator
                        inputValues={inputs}
                        selectedTaxYear={selectedTaxYear}
                      />
                      <CalculatorChart 
                        data={results}
                        selectedTaxYear={selectedTaxYear}
                        hasStudentLoans={inputs.studentLoanPlans && inputs.studentLoanPlans.length > 0}
                      />
                      
                      <CalculatorTable 
                        data={results}
                        selectedTaxYear={selectedTaxYear}
                      />  
                      </>
                    )}
                  </>
                } />
                <Route path="/wizard" element={<Wizard />} />
                <Route 
                  path="/methodology" 
                  element={
                    <Methodology 
                      selectedTaxYear={selectedTaxYear}
                      isTaxResidentInScotland={inputs.isTaxResidentInScotland}
                    />
                  } 
                />
                <Route path="/feedback" element={
                  <React.Suspense fallback={<div style={{ padding: 16 }}>Loading…</div>}>
                    <FeedbackOffers />
                  </React.Suspense>
                } />
                {/* Hidden sandbox route: assistant drawer opens here (env/query gated) */}
                <Route path="/testingAIchatbot" element={
                  <>
                    <Title>AI Assistant Sandbox</Title>
                    <p style={{ marginBottom: 12 }}>This route is unlinked and intended for preview testing.</p>
                    <Suspense fallback={null}>
                      <AssistantLauncherLazy defaultOpen={true} />
                    </Suspense>
                  </>
                } />
              </Routes>
            </div>
          </Content>
          <Footer style={{ textAlign: 'center', paddingBottom: '40px' }} />
        </Layout>
        {/* Assistant gating and lazy-load */}
        {(() => {
          const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
          const sessionEnabled = typeof window !== 'undefined' && window.sessionStorage.getItem('aiEnabled') === '1';
          const queryEnabled = search && search.get('ai') === '1';
          if (queryEnabled && typeof window !== 'undefined') {
            window.sessionStorage.setItem('aiEnabled', '1');
          }
          const assistantEnabled =
            process.env.NODE_ENV !== 'production' ||
            process.env.REACT_APP_ASSISTANT_ENABLED === 'true' ||
            sessionEnabled;
          return assistantEnabled ? (
            <Suspense fallback={null}>
              <AssistantLauncherLazy />
            </Suspense>
          ) : null;
        })()}
      </BrowserRouter>
      <Analytics />
      <SpeedInsights />
    </ConfigProvider>
  );
}

export default App;